import { create } from 'zustand'
import type { ReactNode } from 'react'

export interface ScreenHeaderBack {
  label: string
  onClick: () => void
  /** The glyph before the label. `back` (the default) is the chevron of a
   *  return; `close` is the ✕ of a mode the reader leaves rather than a screen
   *  they return from — the deck screen's selection header uses it for
   *  *Cancel*, which takes the back affordance's slot while cards are
   *  selected (ADR-067, thirteenth amendment). */
  icon?: 'back' | 'close'
}

export interface ScreenHeaderTools {
  /** Full-fidelity render — shown when there's room. */
  full: ReactNode
  /** Icon-only / shrunk render — shown once `full` would leave the screen
   *  name less than SCREEN_HEADER_NAME_MIN_WIDTH of room. Falls back to
   *  `full` when omitted (no degrade path for this screen's tools). */
  compact?: ReactNode
  /** Collapsed-further render (e.g. a single overflow trigger) — shown once
   *  `compact` still doesn't leave enough room. Falls back to `compact` (or
   *  `full`) when omitted. */
  overflow?: ReactNode
}

export interface ScreenHeaderConfig {
  back?: ScreenHeaderBack | null
  title: string
  /**
   * The name's full text as a hover hint, for a name the bar truncates — and,
   * on the deck screen, the deck's description (which the reading pane's
   * resting state also shows in full, so the hint is never the only route).
   */
  titleHint?: string | null
  /**
   * A second, smaller line under the name — the screen's own facts about
   * itself (the deck screen: card count, due, % learned). Part of the
   * identity, so never a control; it truncates with the name and never wraps.
   * The bar keeps its height: two lines fit the same `--header-height`.
   */
  subtitle?: ReactNode
  /** At most one, non-interactive — a state label, never a control. */
  chip?: string | null
  /**
   * The screen's own navigation, centred in the slack between the bar's two
   * ends (DESIGN_SYSTEM.md §10.3). A screen without navigation omits it and
   * the slack is pure space.
   *
   * Separate from `tools` because it is neither an action nor an overflow: it
   * does not belong at the right end, and it must not degrade into a ⋯ — a
   * screen's own tabs are how the reader moves around inside it.
   */
  nav?: ReactNode
  /**
   * The screen's ACTION — the first half of the controls group at the bar's
   * right end, ahead of the overflow (§10.2, ordered least-persistent first).
   *
   * Named for the slot, not for its one current occupant: the deck screen puts
   * a mode control here, and §10.6 is about that case — "a mode swaps the
   * action's own slot and keeps its weight", so both states of a mode are two
   * renders of THIS slot, never two different slots with different weights. It
   * was called `modeBadge` while the deck's mode was the only thing that had
   * ever been put in it, which read as though the bar had a mode concept; it
   * does not, it has an action slot.
   *
   * Separate from `chip` because that one is explicitly a label and never a
   * control, and separate from `tools` because the tools zone degrades to an
   * overflow trigger when the row gets tight (see ScreenHeaderBar) and an
   * action — a mode's off switch above all — must not be able to hide behind a
   * ⋯. A screen that publishes none leaves the slot empty and it collapses;
   * nothing is reserved for it (§10.8).
   */
  action?: ReactNode
  /**
   * The action at its narrowest: its icon, with the label kept as the
   * accessible name and a hint. The LAST rung of the bar's fit ladder, taken
   * only once every other rung is spent AND the name has come down to
   * `SCREEN_HEADER_NAME_NARROW_FLOOR` — so a screen offers it only where it
   * would otherwise lose its name or push a control off the bar (the deck
   * screen, below its two-pane threshold). Same capsule, same weight: §10.6's
   * "a control must not recede" is about weight, and the label is what gives.
   */
  actionCompact?: ReactNode
  /**
   * The screen's own controls placed BEFORE the action, at the head of the
   * controls group (the deck screen: the daily-learning switch and the two
   * study exercises). Tiered like `tools`, and stepped down FIRST: a screen
   * that has put working controls in the bar spends their labels before it
   * spends its overflow's or its return affordance's. `full → compact →
   * overflow` are the screen's own rungs; what each one gives up is the
   * screen's decision, recorded where it publishes them.
   */
  lead?: ScreenHeaderTools | null
  tools?: ScreenHeaderTools | null
}

interface ScreenHeaderState {
  config: ScreenHeaderConfig | null
  setHeader: (config: ScreenHeaderConfig) => void
  clear: () => void
}

export const useScreenHeaderStore = create<ScreenHeaderState>((set) => ({
  config: null,
  setHeader: (config) => set({ config }),
  clear: () => set({ config: null }),
}))
